// apps/web/src/lib/passwordStrength.ts
// Client-side PARTIAL mirror of the server activation password policy
// (apps/api: routes/schemas/auth.ts level 1 + services/passwordPolicy.ts level
// 2). The server is authoritative; this only gates the submit button and drives
// the strength meter for UX — it deliberately CANNOT replicate the level-2
// user-info ban (email/nom/prenom are unknown to the anonymous client: the
// verify endpoint returns only { valid } for anti-enumeration). So a password
// that passes here can still be 400'd server-side; the form surfaces that.
//
// zxcvbn is heavy (~400KB with dictionaries), so it is lazy-loaded on first use
// via dynamic import() and cached in a module-level promise — it never enters
// the initial bundle of the public surface.

export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_MIN_CLASSES = 3;
export const PASSWORD_MIN_ZXCVBN = 3;

// At least N of {lowercase, uppercase, digit, non-alphanumeric}. Mirror of the
// server's countCharClasses (routes/schemas/auth.ts).
export function countCharClasses(value: string): number {
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(value))
    .length;
}

type ZxcvbnChecker = { check: (password: string) => { score: number } };

let checkerPromise: Promise<ZxcvbnChecker> | null = null;

async function getChecker(): Promise<ZxcvbnChecker> {
  if (checkerPromise === null) {
    checkerPromise = (async () => {
      const [core, common, fr] = await Promise.all([
        import('@zxcvbn-ts/core'),
        import('@zxcvbn-ts/language-common'),
        import('@zxcvbn-ts/language-fr'),
      ]);
      return new core.ZxcvbnFactory({
        dictionary: { ...common.dictionary, ...fr.dictionary },
        graphs: common.adjacencyGraphs,
        translations: fr.translations,
      });
    })();
  }
  return checkerPromise;
}

// Lazy zxcvbn score (0..4). No userInputs client-side (identity unknown).
export async function scorePassword(password: string): Promise<number> {
  if (password.length === 0) return 0;
  const checker = await getChecker();
  return checker.check(password).score;
}

// Stateless local checks (length + classes), available synchronously.
export function meetsLocalPolicy(password: string): boolean {
  return (
    password.length >= PASSWORD_MIN_LENGTH &&
    countCharClasses(password) >= PASSWORD_MIN_CLASSES
  );
}
